import { useState } from "react";
import { useSearchParams } from "react-router";
import {
  Loader2,
  RotateCcw,
  Trash2,
  XCircle,
  Play,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertTriangle,
  Plus,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  useSystemJobs,
  useSystemStats,
  useRetryJob,
  useRequeueJob,
  useCancelJob,
  useDeleteJob,
  useRecoverStuck,
  useCleanupJobs,
  useScheduleJob,
  useSystemJobDetail,
  useJobsByWorker,
} from "@/hooks/api";

const PAGE_SIZE = 50;

function statusBadge(status: string) {
  const variant =
    status === "completed"
      ? ("default" as const)
      : status === "failed"
        ? ("destructive" as const)
        : status === "running"
          ? ("secondary" as const)
          : ("outline" as const);
  return <Badge variant={variant}>{status}</Badge>;
}

function computeTotal(
  status: string,
  worker: string,
  stats: any,
  jobsByWorker: any[] | undefined,
): number | null {
  if (worker) {
    if (!jobsByWorker) return null;
    const rows = jobsByWorker.filter((r: any) => r.worker === worker);
    if (rows.length === 0) return 0;
    if (status) {
      return rows.reduce(
        (sum: number, r: any) => sum + (r[status] ?? 0),
        0,
      );
    }
    return rows.reduce((sum: number, r: any) => sum + (r.total ?? 0), 0);
  }
  if (!stats) return null;
  if (status) return stats[status] ?? 0;
  return stats.total ?? null;
}

function pageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const pages: (number | "...")[] = [];
  const last = total - 1;
  pages.push(0);
  const start = Math.max(1, current - 1);
  const end = Math.min(last - 1, current + 1);
  if (start > 1) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < last - 1) pages.push("...");
  pages.push(last);
  return pages;
}

function formatDuration(startedAt: string | null, completedAt: string | null) {
  if (!startedAt) return "-";
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function JobDetailDialog({
  jobId,
  children,
}: {
  jobId: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const detail = useSystemJobDetail(open ? jobId : "");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Jobb #{jobId}</DialogTitle>
        </DialogHeader>
        {detail.isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : detail.data ? (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-admin-text-secondary">Worker:</span>{" "}
                <span className="font-mono">{(detail.data as any).worker}</span>
              </div>
              <div>
                <span className="text-admin-text-secondary">Status:</span>{" "}
                {statusBadge((detail.data as any).status)}
              </div>
              <div>
                <span className="text-admin-text-secondary">Forsok:</span>{" "}
                {(detail.data as any).attempts}/
                {(detail.data as any).max_attempts}
              </div>
              <div>
                <span className="text-admin-text-secondary">Ko:</span>{" "}
                {(detail.data as any).queue}
              </div>
            </div>
            {(detail.data as any).error && (
              <div>
                <p className="text-admin-text-secondary mb-1">Feil:</p>
                <pre className="text-xs bg-admin-bg rounded p-2 text-admin-danger-400 whitespace-pre-wrap">
                  {(detail.data as any).error}
                </pre>
              </div>
            )}
            <div>
              <p className="text-admin-text-secondary mb-1">Args:</p>
              <pre className="text-xs bg-admin-bg rounded p-2 whitespace-pre-wrap">
                {JSON.stringify((detail.data as any).args, null, 2)}
              </pre>
            </div>
            {(detail.data as any).logs?.length > 0 && (
              <div>
                <p className="text-admin-text-secondary mb-1">
                  Logger ({(detail.data as any).logs.length}):
                </p>
                <div className="space-y-1 max-h-60 overflow-auto">
                  {(detail.data as any).logs.map((log: any) => (
                    <div
                      key={log.id}
                      className="text-xs font-mono bg-admin-bg rounded px-2 py-1"
                    >
                      <span
                        className={
                          log.level === "error"
                            ? "text-admin-danger-400"
                            : log.level === "warn"
                              ? "text-admin-caution-400"
                              : "text-admin-text-secondary"
                        }
                      >
                        [{log.level}]
                      </span>{" "}
                      {log.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-admin-text-secondary">Ikke funnet</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScheduleJobDialog({
  workers,
  workerArgsMeta,
}: {
  workers: string[];
  workerArgsMeta: Record<string, any>;
}) {
  const [open, setOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState("");
  const [argsStr, setArgsStr] = useState("{}");
  const scheduleJob = useScheduleJob();

  const meta = selectedWorker ? workerArgsMeta[selectedWorker] : null;

  const handleSubmit = async () => {
    try {
      const args = JSON.parse(argsStr);
      await scheduleJob.mutateAsync({ worker: selectedWorker, args });
      toast.success("Jobb planlagt");
      setOpen(false);
      setSelectedWorker("");
      setArgsStr("{}");
    } catch (err: any) {
      toast.error(err.message || "Feil");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-3 w-3 mr-1" /> Planlegg jobb
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Planlegg ny jobb</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-admin-text-secondary">Worker</label>
            <select
              className="w-full mt-1 bg-admin-bg border border-admin-card-border rounded px-3 py-2 text-sm"
              value={selectedWorker}
              onChange={(e) => {
                setSelectedWorker(e.target.value);
                const m = workerArgsMeta[e.target.value];
                if (m) {
                  const defaults: Record<string, any> = {};
                  for (const [key, field] of Object.entries(m) as [
                    string,
                    any,
                  ][]) {
                    if (field.default !== undefined)
                      defaults[key] = field.default;
                    else if (field.required) defaults[key] = "";
                  }
                  setArgsStr(JSON.stringify(defaults, null, 2));
                } else {
                  setArgsStr("{}");
                }
              }}
            >
              <option value="">Velg worker...</option>
              {workers.slice().sort((a, b) => a.localeCompare(b)).map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
          </div>
          {meta && (
            <div className="text-xs text-admin-text-secondary space-y-0.5">
              {Object.entries(meta).map(([key, field]: [string, any]) => (
                <div key={key}>
                  <span className="font-mono">{key}</span>
                  {field.required && <span className="text-admin-danger-400">*</span>}
                  {field.hint && <span className="ml-1">— {field.hint}</span>}
                </div>
              ))}
            </div>
          )}
          <div>
            <label className="text-sm text-admin-text-secondary">
              Args (JSON)
            </label>
            <textarea
              className="w-full mt-1 bg-admin-bg border border-admin-card-border rounded px-3 py-2 text-sm font-mono min-h-[100px]"
              value={argsStr}
              onChange={(e) => setArgsStr(e.target.value)}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!selectedWorker || scheduleJob.isPending}
            className="w-full"
          >
            {scheduleJob.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Planlegg"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function JobsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") ?? "";
  const workerFilter = searchParams.get("worker") ?? "";
  const page = Math.max(0, parseInt(searchParams.get("page") ?? "0") || 0);

  const updateParams = (next: {
    status?: string;
    worker?: string;
    page?: number;
  }) => {
    const params = new URLSearchParams(searchParams);
    const status = next.status ?? statusFilter;
    const worker = next.worker ?? workerFilter;
    const newPage = next.page ?? page;
    if (status) params.set("status", status);
    else params.delete("status");
    if (worker) params.set("worker", worker);
    else params.delete("worker");
    if (newPage > 0) params.set("page", String(newPage));
    else params.delete("page");
    setSearchParams(params, { replace: true });
  };

  const stats = useSystemStats();
  const jobsByWorker = useJobsByWorker();
  const jobs = useSystemJobs({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    status: statusFilter || undefined,
    worker: workerFilter || undefined,
  });

  const totalCount = computeTotal(
    statusFilter,
    workerFilter,
    stats.data,
    jobsByWorker.data,
  );
  const totalPages =
    totalCount != null ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : null;

  const retryJob = useRetryJob();
  const requeueJob = useRequeueJob();
  const cancelJob = useCancelJob();
  const deleteJob = useDeleteJob();
  const recoverStuck = useRecoverStuck();
  const cleanupJobs = useCleanupJobs();

  const registeredWorkers = (stats.data as any)?.registeredWorkers ?? [];
  const workerArgsMeta = (stats.data as any)?.workerArgsMeta ?? {};

  const handleAction = async (
    action: () => Promise<any>,
    successMsg: string,
  ) => {
    try {
      await action();
      toast.success(successMsg);
    } catch (err: any) {
      toast.error(err.message || "Feil");
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="bg-admin-bg border border-admin-card-border rounded px-3 py-1.5 text-sm"
          value={statusFilter}
          onChange={(e) =>
            updateParams({ status: e.target.value, page: 0 })
          }
        >
          <option value="">Alle statuser</option>
          <option value="pending">Ventende</option>
          <option value="running">Kjorer</option>
          <option value="completed">Fullfort</option>
          <option value="failed">Feilet</option>
        </select>

        <select
          className="bg-admin-bg border border-admin-card-border rounded px-3 py-1.5 text-sm"
          value={workerFilter}
          onChange={(e) =>
            updateParams({ worker: e.target.value, page: 0 })
          }
        >
          <option value="">Alle workers</option>
          {registeredWorkers
            .slice()
            .sort((a: string, b: string) => a.localeCompare(b))
            .map((w: string) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
        </select>

        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              jobs.refetch();
              stats.refetch();
              jobsByWorker.refetch();
            }}
            disabled={jobs.isFetching}
            title="Oppdater"
          >
            <RefreshCw
              className={`h-3 w-3 mr-1 ${jobs.isFetching ? "animate-spin" : ""}`}
            />{" "}
            Oppdater
          </Button>
          <ScheduleJobDialog
            workers={registeredWorkers}
            workerArgsMeta={workerArgsMeta}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              handleAction(
                () => recoverStuck.mutateAsync(),
                "Stuck-jobber gjenopprettet",
              )
            }
            disabled={recoverStuck.isPending}
          >
            <AlertTriangle className="h-3 w-3 mr-1" /> Gjenopprett stuck
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              handleAction(
                () => cleanupJobs.mutateAsync({ daysToKeep: 7 }),
                "Opprydding fullfort",
              )
            }
            disabled={cleanupJobs.isPending}
          >
            <Trash2 className="h-3 w-3 mr-1" /> Rydd opp
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          {jobs.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Worker</TableHead>
                    <TableHead>Ko</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Forsok</TableHead>
                    <TableHead>Planlagt</TableHead>
                    <TableHead>Varighet</TableHead>
                    <TableHead>Feil</TableHead>
                    <TableHead>Handlinger</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(jobs.data ?? []).map((job: any) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <JobDetailDialog jobId={String(job.id)}>
                          <button className="text-admin-primary hover:underline cursor-pointer font-mono text-xs">
                            #{job.id}
                          </button>
                        </JobDetailDialog>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {job.worker}
                      </TableCell>
                      <TableCell className="text-xs">{job.queue}</TableCell>
                      <TableCell>{statusBadge(job.status)}</TableCell>
                      <TableCell className="text-xs">
                        {job.attempts}/{job.max_attempts}
                      </TableCell>
                      <TableCell className="text-xs">
                        {new Date(job.scheduled_at).toLocaleString("nb-NO", {
                          hour: "2-digit",
                          minute: "2-digit",
                          day: "2-digit",
                          month: "short",
                        })}
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatDuration(job.started_at, job.completed_at)}
                      </TableCell>
                      <TableCell className="text-xs max-w-[200px] truncate text-admin-danger-400">
                        {job.error || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {job.status === "failed" && (
                            <button
                              onClick={() =>
                                handleAction(
                                  () => retryJob.mutateAsync(String(job.id)),
                                  "Jobb forsokes pa nytt",
                                )
                              }
                              className="text-admin-text-secondary hover:text-admin-primary cursor-pointer"
                              title="Forsok pa nytt"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {job.status === "pending" && (
                            <button
                              onClick={() =>
                                handleAction(
                                  () => cancelJob.mutateAsync(String(job.id)),
                                  "Jobb avbrutt",
                                )
                              }
                              className="text-admin-text-secondary hover:text-admin-caution-400 cursor-pointer"
                              title="Avbryt"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {job.status === "running" && (
                            <button
                              onClick={() =>
                                handleAction(
                                  () => requeueJob.mutateAsync(String(job.id)),
                                  "Jobb rekoet",
                                )
                              }
                              className="text-admin-text-secondary hover:text-admin-info-400 cursor-pointer"
                              title="Reko"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {job.status !== "running" && (
                            <button
                              onClick={() =>
                                handleAction(
                                  () => deleteJob.mutateAsync(String(job.id)),
                                  "Jobb slettet",
                                )
                              }
                              className="text-admin-text-secondary hover:text-admin-danger-400 cursor-pointer"
                              title="Slett"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(jobs.data ?? []).length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="text-center text-admin-text-secondary py-8"
                      >
                        Ingen jobber funnet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
                <p className="text-xs text-admin-text-secondary">
                  Side {page + 1}
                  {totalPages != null ? ` av ${totalPages}` : ""} — Viser{" "}
                  {(jobs.data ?? []).length}
                  {totalCount != null ? ` av ${totalCount}` : ""} jobber
                </p>
                <div className="flex gap-1 items-center flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => updateParams({ page: 0 })}
                    title="Forste side"
                  >
                    <ChevronsLeft className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => updateParams({ page: page - 1 })}
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  {totalPages != null &&
                    pageNumbers(page, totalPages).map((p, i) =>
                      p === "..." ? (
                        <span
                          key={`ellipsis-${i}`}
                          className="text-xs text-admin-text-secondary px-1"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={p}
                          variant={p === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateParams({ page: p })}
                          className="min-w-[2rem] px-2"
                        >
                          {p + 1}
                        </Button>
                      ),
                    )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      totalPages != null
                        ? page >= totalPages - 1
                        : (jobs.data ?? []).length < PAGE_SIZE
                    }
                    onClick={() => updateParams({ page: page + 1 })}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={totalPages == null || page >= totalPages - 1}
                    onClick={() =>
                      totalPages != null &&
                      updateParams({ page: totalPages - 1 })
                    }
                    title="Siste side"
                  >
                    <ChevronsRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
